import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store'
import { selectAuthUser } from '../../../store/selectors/authSelectors'
import { selectNotifiedActivityIds, selectTestNotificationTrigger } from '../../../store/selectors/activitiesSelectors'
import { showGameHudReminder } from '../../../store/slices/activitiesSlice'
import { isGamePagePath } from '../../../config/routes/routeConfig'
import { NextActivityReminderModal } from './NextActivityReminderModal'
import { FeedbackReadyNotification } from './FeedbackReadyNotification'
import { SlowNetworkNotification } from './SlowNetworkNotification'
import { DebriefStartedNotification } from './DebriefStartedNotification'
import { NotificationWrapper } from './NotificationWrapper'
import { getReminderActivityName, useActivityNotifications } from '../../hooks/useActivityNotifications'
import { useFeedbackReadyNotifications } from '../../hooks/useFeedbackReadyNotifications'
import { useSlowNetworkNotice } from '../../hooks/useSlowNetworkNotice'
import { useDebriefStartedNotice } from '../../hooks/useDebriefStartedNotice'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { getActiveOrgRole, isParticipantRole } from '../../../features/organization/orgStateHelpers'

/**
 * ActivityNotificationContainer
 * Raises the next-activity reminder (a modal, or the game's HUD while playing) and shows the debrief-started and
 * feedback-ready (gamer) toasts and the slow-network one in the top-right corner.
 */
export const ActivityNotificationContainer: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectAuthUser)
  const notifiedActivityIds = useAppSelector(selectNotifiedActivityIds)
  const testNotificationTrigger = useAppSelector(selectTestNotificationTrigger)

  const orgRole = getActiveOrgRole(user)
  const isParticipant = isParticipantRole(user)

  const { notifications, dismissNotification, viewNotification } = useActivityNotifications({
    notifiedActivityIds,
    orgRole,
    testNotificationTrigger
  })

  const { feedbackReadyNotifications, dismissFeedbackReady, viewFeedbackReady } = useFeedbackReadyNotifications()
  const { isSlowNetworkNoticeVisible, dismissSlowNetworkNotice } = useSlowNetworkNotice()
  const { isDebriefStartedNoticeVisible, dismissDebriefStartedNotice } = useDebriefStartedNotice(isParticipant)

  // One reminder at a time, the newest; anything older has been overtaken by it.
  const currentReminder = isParticipant ? notifications[notifications.length - 1] : undefined
  const isOnGamePage = isGamePagePath(location.pathname)

  // In the game the page is hidden behind Unity's fullscreen canvas, so the reminder goes to the game's HUD.
  useEffect(() => {
    if (!currentReminder || !isOnGamePage) return
    dispatch(
      showGameHudReminder({
        id: currentReminder.id,
        message: t(
          'gamer.notifications.inGameReminder',
          '{{activity}} starts in {{minutes}} min. Finish up and head to your dashboard.',
          {
            activity: getReminderActivityName(currentReminder.activity, t),
            minutes: currentReminder.minutesBeforeStart
          }
        )
      })
    )
    dismissNotification(currentReminder.id)
  }, [currentReminder, isOnGamePage, dispatch, dismissNotification, t])

  const showActivityReminder = Boolean(currentReminder) && !isOnGamePage
  const showFeedbackReady = isParticipant && feedbackReadyNotifications.length > 0

  // A slow connection hurts facilitators running a session just as much as it hurts players.
  const hasToasts = isDebriefStartedNoticeVisible || showFeedbackReady || isSlowNetworkNoticeVisible
  if (!hasToasts && !showActivityReminder) return null

  return (
    <>
      {showActivityReminder && currentReminder ? (
        <NextActivityReminderModal
          key={currentReminder.id}
          activity={currentReminder.activity}
          minutesBeforeStart={currentReminder.minutesBeforeStart}
          onDismiss={() => dismissNotification(currentReminder.id)}
          onNavigate={() => viewNotification(currentReminder.id)}
        />
      ) : null}
      {hasToasts ? (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            pointerEvents: 'none',
            maxWidth: '400px'
          }}
        >
          {isDebriefStartedNoticeVisible && (
            <NotificationWrapper>
              <DebriefStartedNotification onDismiss={dismissDebriefStartedNotice} />
            </NotificationWrapper>
          )}
          {showFeedbackReady &&
            feedbackReadyNotifications.map((item) => (
              <NotificationWrapper key={item.id}>
                <FeedbackReadyNotification
                  sessionId={item.sessionId}
                  onDismiss={() => dismissFeedbackReady(item.id)}
                  onNavigate={() => viewFeedbackReady(item.id)}
                  autoDismissAfter={10000}
                />
              </NotificationWrapper>
            ))}
          {isSlowNetworkNoticeVisible && (
            <NotificationWrapper>
              <SlowNetworkNotification onDismiss={dismissSlowNetworkNotice} />
            </NotificationWrapper>
          )}
        </Box>
      ) : null}
    </>
  )
}
